/**
 * Visual defect map drawn over a card's art. Each sub-grade below 10 places a
 * marker where a grader would note the flaw: corner nicks in the four corners,
 * edge wear along the borders, surface marks across the middle and a centring
 * frame when the print is off-register. Purely decorative — the readable list
 * of defects lives next to it in text.
 */
export interface DefectMap {
  centering?: number | null;
  corners?: number | null;
  edges?: number | null;
  surface?: number | null;
  defects?: string[] | null;
}

function sev(v: number) {
  // 10 = flawless, 6 or lower = heavy. Returns 0..1 marker strength.
  return Math.min(1, Math.max(0, (10 - v) / 4));
}

export function DefectOverlay({ card, className = "" }: { card: DefectMap; className?: string }) {
  const centering = Number(card.centering ?? 10);
  const corners = Number(card.corners ?? 10);
  const edges = Number(card.edges ?? 10);
  const surface = Number(card.surface ?? 10);

  const showCorners = corners < 10;
  const showEdges = edges < 10;
  const showSurface = surface < 10;
  const showCentering = centering < 10;
  if (!showCorners && !showEdges && !showSurface && !showCentering) return null;

  // Precise instrument palette rather than cartoon highlighter: thin amber for
  // light wear, thin rose for heavy, with a soft halo instead of thick strokes.
  const amber = "#f5c451";
  const rose = "#fb7185";
  const tone = (v: number) => (v < 8 ? rose : amber);
  const uid = `dfx-${Math.round(centering * 10)}-${Math.round(corners * 10)}-${Math.round(edges * 10)}-${Math.round(surface * 10)}`;

  return (
    <span
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 ${className}`}
      data-defect-overlay=""
    >
      <svg viewBox="0 0 100 140" preserveAspectRatio="none" className="h-full w-full">
        <defs>
          <filter id={`${uid}-glow`} x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="0.7" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <linearGradient id={`${uid}-edge`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={tone(edges)} stopOpacity="0" />
            <stop offset="50%" stopColor={tone(edges)} stopOpacity="0.95" />
            <stop offset="100%" stopColor={tone(edges)} stopOpacity="0" />
          </linearGradient>
        </defs>

        {showCentering && (
          <g filter={`url(#${uid}-glow)`}>
            <rect
              x={2 + sev(centering) * 2.4}
              y={2 + sev(centering) * 2.4}
              width={96 - sev(centering) * 4.8}
              height={136 - sev(centering) * 4.8}
              rx={1.4}
              fill="none"
              stroke={tone(centering)}
              strokeWidth={0.45}
              strokeDasharray="2.4 2.2"
              opacity={0.85}
            />
          </g>
        )}

        {showEdges && (
          <g filter={`url(#${uid}-glow)`} opacity={0.9}>
            {/* Tapered wear bands hugging the trim line. */}
            <rect x={12} y={0.7} width={30} height={0.55 + sev(edges) * 0.5} fill={`url(#${uid}-edge)`} />
            <rect x={58} y={138.75 - sev(edges) * 0.5} width={30} height={0.55 + sev(edges) * 0.5} fill={`url(#${uid}-edge)`} />
            <rect
              x={99.45 - sev(edges) * 0.5}
              y={40}
              width={0.55 + sev(edges) * 0.5}
              height={34}
              fill={tone(edges)}
              opacity={0.9}
            />
            <rect x={0} y={86} width={0.55 + sev(edges) * 0.5} height={24} fill={tone(edges)} opacity={0.9} />
          </g>
        )}

        {showCorners && (
          <g fill="none" stroke={tone(corners)} strokeWidth={0.55} strokeLinejoin="round" opacity={0.95} filter={`url(#${uid}-glow)`}>
            {[
              [2.4, 2.4, 1, 1],
              [97.6, 2.4, -1, 1],
              [2.4, 137.6, 1, -1],
              [97.6, 137.6, -1, -1],
            ].map(([x, y, dx, dy], i) => {
              const l = 5 + sev(corners) * 5;
              return (
                <g key={i}>
                  {/* Precision reticle bracket. */}
                  <path d={`M${x} ${y + dy * l} L${x} ${y} L${x + dx * l} ${y}`} />
                  <path d={`M${x + dx * 1.6} ${y + dy * (l * 0.55)} L${x + dx * 1.6} ${y + dy * 1.6} L${x + dx * (l * 0.55)} ${y + dy * 1.6}`} opacity={0.45} />
                </g>
              );
            })}
          </g>
        )}

        {showSurface && (
          <g filter={`url(#${uid}-glow)`}>
            {/* Fine hairline scratches plus a measured ring around the mark. */}
            <g stroke={tone(surface)} strokeLinecap="round" opacity={0.9}>
              <line x1={27} y1={51} x2={45} y2={63.5} strokeWidth={0.4} />
              <line x1={30} y1={56} x2={40} y2={62} strokeWidth={0.28} opacity={0.7} />
              <line x1={58} y1={82} x2={73} y2={92} strokeWidth={0.4} />
            </g>
            <circle
              cx={38}
              cy={98}
              r={2 + sev(surface) * 1.8}
              fill="none"
              stroke={tone(surface)}
              strokeWidth={0.45}
              strokeDasharray="1.6 1.4"
              opacity={0.9}
            />
            <circle cx={38} cy={98} r={0.6} fill={tone(surface)} opacity={0.95} />
          </g>
        )}
      </svg>
    </span>
  );
}


/** Small caption listing which sub-grades produced the markers. */
export function defectMarkerLegend(card: DefectMap): string[] {
  const out: string[] = [];
  if (Number(card.centering ?? 10) < 10) out.push("dashed frame = off-centre print");
  if (Number(card.corners ?? 10) < 10) out.push("corner brackets = corner wear");
  if (Number(card.edges ?? 10) < 10) out.push("border strokes = edge wear");
  if (Number(card.surface ?? 10) < 10) out.push("scratches = surface marks");
  return out;
}

/**
 * Mobile-first defect art view: the card art at full container width with the
 * defect map drawn over it and the marker legend underneath, sized for a
 * thumb-held phone first and only capped on wider screens.
 */
export function DefectArtView({
  card,
  image,
  name,
  className = "",
}: {
  card: DefectMap;
  image?: string;
  name?: string;
  className?: string;
}) {
  if (!image) return null;
  const legend = defectMarkerLegend(card);
  return (
    <span className={`block ${className}`}>
      <span className="relative mx-auto block w-full max-w-[20rem] overflow-hidden rounded-lg border">
        <img
          src={image}
          alt={`${name ?? "Palworld card"} with its graded defects marked on the art`}
          width={300}
          height={420}
          loading="lazy"
          decoding="async"
          className="block h-auto w-full"
        />
        <DefectOverlay card={card} />
      </span>
      <span className="mt-1.5 block text-center text-[11px] leading-snug text-muted-foreground sm:text-[10px]">
        {legend.length ? `On the art: ${legend.join(" · ")}.` : "No defect markers — flawless across every sub-grade."}
      </span>
    </span>
  );
}
